/**
 * Global SVG mask registry. Masks are registered by id; the last registration
 * for a given id is the one rendered in the global defs. Components unregister
 * on unmount so the previous registration (if any) is restored.
 *
 * State: Map<id, Array<{ owner, content }>> — one stack per mask ID.
 * The notifier is triggered by the consumer's useLayoutEffect (not from
 * register/unregister) to avoid setState during render.
 */

/** @type {Map<string, Array<{ owner: object, content: import('react').ReactNode }>>} */
const stacksById = new Map();

/** @type {((snapshot: Map<string, import('react').ReactNode>) => void) | null} */
let notifier = null;

/**
 * Register or update a mask for the given id.
 * If this owner already has an entry for this id, content is updated in place.
 * Otherwise the registration is pushed onto the stack (last registration wins).
 *
 * @param {string} id - Mask id (used in url(#id)).
 * @param {import('react').ReactNode} content - Children of the <mask> element.
 * @param {object} owner - Opaque token identifying the component instance (e.g. a ref).
 */
export function register(id, content, owner) {
  let stack = stacksById.get(id);
  if (!stack) {
    stack = [];
    stacksById.set(id, stack);
  }

  const existing = stack.find((entry) => entry.owner === owner);
  if (existing) {
    existing.content = content;
  } else {
    stack.push({ owner, content });
  }
}

/**
 * Remove this owner's registration for the given id.
 *
 * @param {string} id - Mask id.
 * @param {object} owner - Same token passed to register.
 */
export function unregister(id, owner) {
  const stack = stacksById.get(id);
  if (!stack) return;

  const index = stack.findIndex((entry) => entry.owner === owner);
  if (index !== -1) {
    stack.splice(index, 1);
    if (stack.length === 0) {
      stacksById.delete(id);
    }
  }
}

/**
 * Current snapshot for rendering: map of id -> top-of-stack content.
 * Entries with an empty stack are omitted.
 *
 * @returns {Map<string, import('react').ReactNode>}
 */
export function getSnapshot() {
  const snapshot = new Map();
  for (const [id, stack] of stacksById.entries()) {
    if (stack.length > 0) {
      snapshot.set(id, stack[stack.length - 1].content);
    }
  }
  return snapshot;
}

/**
 * Set the notifier called when the registry changes. The consumer's
 * useLayoutEffect should call notify() after register/unregister.
 *
 * @param {((snapshot: Map<string, import('react').ReactNode>) => void) | null} fn
 */
export function setNotifier(fn) {
  notifier = fn;
}

/**
 * Notify the global component to re-render with the current snapshot.
 * Call this from useLayoutEffect after register or unregister.
 */
export function notify() {
  if (notifier) {
    notifier(getSnapshot());
  }
}
